import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  getPriceListAdjustedBasePrice,
  isProductVisibleInPriceList,
  toPosPricingProduct,
} from './priceLists.js';

// Exportación de una lista de precios para mandar a clientes: PDF para
// imprimir y WhatsApp. Usa el MISMO cálculo que POS/Tienda
// (getPriceListAdjustedBasePrice), así lo que recibe el cliente es lo que
// después se le cobra. Nunca incluye costos.
//
// El formato del PDF se configura en Configuración > Reportes y Tickets >
// "Lista de precios" (organizations.print_config.lista_precios).

export const PRICE_LIST_DOC_TYPE = 'lista_precios';

export const DEFAULT_PRICE_LIST_PRINT_CONFIG = {
  headerText: '',
  subHeaderText: '',
  footerText: 'Precios sujetos a modificación sin previo aviso.',
  showLogo: true,
  logoBase64: null,
  printerWidth: 'a4',
  showBrand: true,
  showBarcode: false,
  showListDate: true,
  showListName: true,
  whatsappMessage: 'Hola {cliente}! Te comparto nuestra lista de precios {lista} actualizada al {fecha}.',
  fieldsOrder: ['showLogo', 'showListName', 'showListDate', 'showBrand', 'showBarcode'],
};

export function getPriceListPrintConfig(organization) {
  const saved = organization?.print_config?.[PRICE_LIST_DOC_TYPE] || {};
  const config = { ...DEFAULT_PRICE_LIST_PRINT_CONFIG, ...saved };
  if (!String(config.headerText || '').trim()) config.headerText = getOrganizationDisplayName(organization);
  return config;
}

const money = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayLabel = () => new Date().toLocaleDateString('es-AR');

/**
 * Renglones exportables: productos visibles en la lista, con precio > 0,
 * ordenados por nombre.
 */
export function buildPriceListExportRows(products, priceList, usdExchangeRate) {
  return (Array.isArray(products) ? products : [])
    .filter((product) => product?.id && isProductVisibleInPriceList(product.id, priceList))
    .map((product) => ({
      id: product.id,
      name: String(product.name || 'SIN NOMBRE').trim(),
      brand: product.brand ? String(product.brand).trim() : '',
      barcode: product.barcode ? String(product.barcode).trim() : '',
      // Misma base que el POS (USD convertido, sin variantes).
      price: getPriceListAdjustedBasePrice(toPosPricingProduct(product, usdExchangeRate), priceList),
    }))
    .filter((row) => Number.isFinite(row.price) && row.price > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

function safeFileName(name) {
  return String(name || 'lista')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'lista';
}

export function getPriceListFileName(priceList) {
  return `lista-precios-${safeFileName(priceList?.name)}.pdf`;
}

export function getOrganizationDisplayName(organization) {
  return String(organization?.name || organization?.razon_social || organization?.business_name || '').trim();
}

function imageFormat(dataUrl) {
  const match = /^data:image\/(png|jpe?g|webp)/i.exec(String(dataUrl || ''));
  if (!match) return null;
  const fmt = match[1].toLowerCase();
  return fmt === 'jpg' ? 'JPEG' : fmt.toUpperCase();
}

function tableColumns(config, rows) {
  const cols = [{ key: 'name', title: 'Producto' }];
  if (config.showBrand && rows.some((r) => r.brand)) cols.push({ key: 'brand', title: 'Marca' });
  if (config.showBarcode && rows.some((r) => r.barcode)) cols.push({ key: 'barcode', title: 'Código' });
  cols.push({ key: 'price', title: 'Precio' });
  return cols;
}

export function buildPriceListPdf({ priceList, rows, organization, config: configOverride }) {
  const config = configOverride || getPriceListPrintConfig(organization);
  const isTicket = config.printerWidth === '80mm' || config.printerWidth === '58mm';
  const ticketWidth = config.printerWidth === '58mm' ? 58 : 80;
  const cols = tableColumns(config, rows);
  const logoFmt = config.showLogo ? imageFormat(config.logoBase64) : null;

  // En ticket el alto de la hoja depende de la cantidad de renglones.
  const doc = isTicket
    ? new jsPDF({ unit: 'mm', format: [ticketWidth, Math.max(80, 55 + rows.length * 6 + (logoFmt ? 22 : 0))] })
    : new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const margin = isTicket ? 3 : 14;
  const center = W / 2;
  let y = isTicket ? 6 : 14;

  if (logoFmt) {
    try {
      const props = doc.getImageProperties(config.logoBase64);
      const maxW = isTicket ? ticketWidth - 20 : 40;
      const maxH = isTicket ? 18 : 20;
      const ratio = Math.min(maxW / props.width, maxH / props.height);
      const w = props.width * ratio;
      const h = props.height * ratio;
      doc.addImage(config.logoBase64, logoFmt, isTicket ? center - w / 2 : margin, y, w, h);
      y += h + 3;
    } catch {
      // Logo inválido: se sigue sin logo.
    }
  }

  const align = isTicket ? 'center' : 'left';
  const x = isTicket ? center : margin;
  if (config.headerText) {
    doc.setFont(undefined, 'bold');
    doc.setFontSize(isTicket ? 11 : 16);
    doc.text(String(config.headerText).toUpperCase(), x, y, { align, maxWidth: W - margin * 2 });
    y += isTicket ? 5 : 7;
  }
  if (config.subHeaderText) {
    doc.setFont(undefined, 'normal');
    doc.setFontSize(isTicket ? 7 : 9);
    const lines = doc.splitTextToSize(String(config.subHeaderText), W - margin * 2);
    doc.text(lines, x, y, { align });
    y += lines.length * (isTicket ? 3 : 4) + 1;
  }
  doc.setFont(undefined, 'bold');
  doc.setFontSize(isTicket ? 9 : 12);
  const title = config.showListName && priceList?.name
    ? `LISTA DE PRECIOS: ${String(priceList.name).toUpperCase()}`
    : 'LISTA DE PRECIOS';
  doc.text(title, x, y + 2, { align, maxWidth: W - margin * 2 });
  doc.setFont(undefined, 'normal');
  if (config.showListDate) {
    doc.setFontSize(isTicket ? 7 : 9);
    if (isTicket) {
      y += 5;
      doc.text(`Fecha: ${todayLabel()}`, center, y + 2, { align: 'center' });
    } else {
      doc.text(`Fecha: ${todayLabel()}`, W - margin, y + 2, { align: 'right' });
    }
  }
  y += isTicket ? 5 : 6;

  const priceCol = cols.length - 1;
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [cols.map((c) => c.title)],
    body: rows.map((row) => cols.map((c) => (c.key === 'price' ? money(row.price) : row[c.key] || ''))),
    styles: { fontSize: isTicket ? 7 : 9, cellPadding: isTicket ? 1 : 2 },
    headStyles: isTicket ? { fillColor: false, textColor: 0, fontStyle: 'bold' } : { fillColor: [11, 42, 54], textColor: 255 },
    alternateRowStyles: isTicket ? {} : { fillColor: [245, 247, 250] },
    theme: isTicket ? 'plain' : 'striped',
    columnStyles: { [priceCol]: { halign: 'right', cellWidth: isTicket ? 20 : 35 } },
    didDrawPage: () => {
      if (isTicket) return;
      const H = doc.internal.pageSize.getHeight();
      doc.setFontSize(7);
      doc.setTextColor(140);
      if (config.footerText) doc.text(String(config.footerText), margin, H - 8, { maxWidth: W - 50 });
      doc.text(`Página ${doc.internal.getNumberOfPages()}`, W - margin, H - 8, { align: 'right' });
      doc.setTextColor(0);
    },
  });

  if (isTicket && config.footerText) {
    const endY = (doc.lastAutoTable?.finalY || y) + 5;
    doc.setFontSize(7);
    doc.text(doc.splitTextToSize(String(config.footerText), W - margin * 2), center, endY, { align: 'center' });
  }

  return doc;
}

/** Abre el PDF con el diálogo de impresión (desde ahí también se guarda). */
export function printPriceList({ priceList, rows, organization }) {
  const doc = buildPriceListPdf({ priceList, rows, organization });
  doc.autoPrint();
  const opened = window.open(doc.output('bloburl'), '_blank');
  if (!opened) doc.save(getPriceListFileName(priceList));
  return opened ? 'opened' : 'downloaded';
}

/**
 * Normaliza un teléfono argentino a formato wa.me (sin +, con 549).
 * Vacío = sin destinatario (WhatsApp deja elegir el contacto).
 */
export function normalizeWhatsAppPhone(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  if (digits.startsWith('54')) {
    const rest = digits.slice(2);
    return rest.startsWith('9') ? digits : `549${rest}`;
  }
  // Celular cargado con 15 (ej: 341 15 5551234): se saca el 15.
  if (digits.length === 12 && digits.slice(3, 5) === '15') digits = digits.slice(0, 3) + digits.slice(5);
  else if (digits.length === 12 && digits.slice(2, 4) === '15') digits = digits.slice(0, 2) + digits.slice(4);
  else if (digits.length === 12 && digits.slice(4, 6) === '15') digits = digits.slice(0, 4) + digits.slice(6);
  if (digits.length === 10) return `549${digits}`;
  // Muy corto para ser un celular con característica: no sirve para WhatsApp.
  return digits.length >= 11 ? digits : '';
}

/** Arma el mensaje de WhatsApp a partir de la plantilla configurada. */
export function buildPriceListWhatsAppMessage({ template, priceList, organization, clientName }) {
  const base = String(template || DEFAULT_PRICE_LIST_PRINT_CONFIG.whatsappMessage);
  return base
    .replaceAll('{cliente}', String(clientName || '').trim())
    .replaceAll('{lista}', String(priceList?.name || '').toUpperCase())
    .replaceAll('{empresa}', getOrganizationDisplayName(organization))
    .replaceAll('{fecha}', todayLabel())
    // Plantillas guardadas con la versión que mandaba link: el PDF va adjunto.
    .replaceAll('{link}', '')
    // "Hola !" cuando no hay cliente
    .replace(/ +([!,.:])/g, '$1')
    .trim();
}

function waUrl(phone, text) {
  const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
  return `${base}?text=${encodeURIComponent(text)}`;
}

/** Celular / tablet (donde el menú de compartir lista WhatsApp). */
export function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  // iPadOS se presenta como Mac con pantalla táctil.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

function buildPdfFile({ priceList, rows, organization, config }) {
  const doc = buildPriceListPdf({ priceList, rows, organization, config });
  const file = typeof File !== 'undefined'
    ? new File([doc.output('blob')], getPriceListFileName(priceList), { type: 'application/pdf' })
    : null;
  return { doc, file };
}

/**
 * Manda la lista por WhatsApp como ARCHIVO PDF (no link).
 * WhatsApp no permite que una web adjunte un archivo al chat de un número, así que:
 * - Celular/tablet: menú de compartir con el PDF adjunto (WhatsApp → contacto).
 * - Computadora: descarga el PDF y abre el chat del cliente (o WhatsApp para
 *   elegir contacto) con el mensaje escrito; se arrastra el PDF y se envía.
 * Llamar directo en el click (sin awaits antes): el navegador exige el gesto.
 * Devuelve 'shared' | 'cancelled' | 'download_chat' | 'download_only'.
 */
export async function sendPriceListWhatsApp({ priceList, rows, organization, phone, clientName }) {
  const config = getPriceListPrintConfig(organization);
  const { doc, file } = buildPdfFile({ priceList, rows, organization, config });
  const text = buildPriceListWhatsAppMessage({ template: config.whatsappMessage, priceList, organization, clientName });

  if (isMobileDevice() && file && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text, title: `Lista de precios ${priceList?.name || ''}`.trim() });
      return 'shared';
    } catch (error) {
      if (error?.name === 'AbortError') return 'cancelled';
      // NotAllowedError u otro: sigue con descarga + chat.
    }
  }

  doc.save(getPriceListFileName(priceList));
  const url = waUrl(normalizeWhatsAppPhone(phone), text);
  const opened = !!window.open(url, '_blank');
  return opened ? 'download_chat' : 'download_only';
}
